/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useRef, useState } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import Select from "react-select";
import apiService from "services/apiService";
import GenericModal from "components/Modal";
import { colors } from "themes";
import { customSelectStyles } from "util/selectStyles";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";
import PhoneNumberInput from "components/PhoneNumberInput";
import {
  DEFAULT_COUNTRY_CODE,
  joinPhoneNumber,
  phoneNumberSchema,
  splitPhoneNumber,
} from "util/phoneCountry";
import { useAppContext } from "contexts/useAppContext";
import { parseJwtPayload, isAdminRole } from "util/moduleAccess";

// An employee is treated as "onboarding" when their account STATUS is "ONBOARDING".
// (It is a userStatus value — NOT a designation.) Match is case-insensitive.
const ONBOARDING = "onboarding";
const isOnboardingValue = (value?: string) =>
  typeof value === "string" && value.trim().toLowerCase() === ONBOARDING;

// UAN format: exactly 12 digits. `excludeEmptyString` is critical — without it
// this yup version fails `matches` on an empty string. UAN is optional for every
// employee, so the pattern only runs once a value is actually typed.
const UAN_REGEX = /^[0-9]{12}$/;
const UAN_FORMAT_MESSAGE = "UAN must be exactly 12 digits";
const uanBase = () =>
  yup
    .string()
    .trim()
    .matches(UAN_REGEX, { message: UAN_FORMAT_MESSAGE, excludeEmptyString: true });

// Shared `.when("userStatus", …)` config: relax a field to optional while the
// account status is "ONBOARDING", otherwise keep it required. This is reactive —
// switching the Status dropdown away from Onboarding re-imposes the normal rules,
// so completing onboarding requires the full set of details.
const optionalWhenOnboarding = (requiredMessage: string) => ({
  is: isOnboardingValue,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  then: (s: any) => s.optional(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  otherwise: (s: any) => s.required(requiredMessage),
});

const schema = yup.object().shape({
  // userStatus always has a value (ACTIVE / INACTIVE / ONBOARDING), so it stays
  // required. It must NOT gate on itself (that would be a cyclic dependency) — it
  // is instead the field every other rule below gates on.
  userStatus: yup.string().required("Status is required."),
  designation: yup
    .string()
    .when("userStatus", optionalWhenOnboarding("designation Is Required.")),
  gender: yup.string().when("userStatus", optionalWhenOnboarding("Gender is required.")),
  displayName: yup
    .string()
    .when("userStatus", optionalWhenOnboarding("Employee Name is Required.")),
  // DOJ default can be null (no joining date yet), so keep the base nullable.
  DOJ: yup
    .string()
    .nullable()
    .when("userStatus", optionalWhenOnboarding("Date Of Joining is required.")),
  // Email format still validated when present; only the required-ness is gated.
  email: validateNoTrailingOrLeadingWhitespace("Email")
    .email("Invalid email format")
    .when("userStatus", optionalWhenOnboarding("Email Is Required.")),
  // phoneNumberSchema without requiredMessage passes on empty; required is added
  // conditionally so the number format check still runs when a value is typed.
  mobileNumber: phoneNumberSchema("mobileCountryCode").when(
    "userStatus",
    optionalWhenOnboarding("Mobile Number Is Required ."),
  ),
  mobileCountryCode: yup.string().optional().default(DEFAULT_COUNTRY_CODE),
  role: yup.string().when("userStatus", optionalWhenOnboarding("Role is required.")),
  // --- UAN VALIDATION --- Optional for every employee; the 12-digit format is
  // only enforced once a value is actually typed (excludeEmptyString on the regex).
  UAN: uanBase().optional(),
});

interface AddProps {
  onClose: () => void;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // The optional second arg carries the updated employee so a caller (e.g. the
  // details screen) can refresh its view without an extra fetch. Callers that
  // only need the message (e.g. the list) can ignore it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess: (message: string, updatedEmployee?: any) => void;
  onError: (message: string) => void;
}
interface GenderOption {
  value: string;
  label: string;
}

interface StoreResponseProp {
  projectNames: string[];
  assetModels: string[];
}
interface userOptions {
  value: number;
  label: string;
}

export default function EditEmployee({ onClose, data, onSuccess, onError }: AddProps) {
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      ...data,
      mobileNumber: splitPhoneNumber(data.mobileNumber).nationalNumber,
      mobileCountryCode: splitPhoneNumber(data.mobileNumber).countryCode,
      DOJ: data.DOJ ? new Date(data.DOJ).toISOString().split("T")[0] : null,
      // Initialize UAN (checks profileDetails first, then falls back to root level)
      UAN: data?.profileDetails?.UAN || data?.UAN || "",
    },
  });
  const [isUpdate, setIsUpdate] = useState(false);
  const {
    formState: { isDirty, isSubmitting },
  } = methods;

  // Check if the current employee being edited is Inactive
  const isInactiveEmployee = data.userStatus === "INACTIVE";

  // Hide the Status and Role fields when an admin is editing their OWN record,
  // so an admin cannot deactivate their own account or change their own role.
  // The values still travel in the payload (they stay in the form defaults),
  // they just can't be edited here.
  const { user, email: loggedInEmail, userRole } = useAppContext();
  const storedUserEmail = (() => {
    try {
      const stored = typeof window !== "undefined" ? localStorage.getItem("user") : null;
      return stored ? JSON.parse(stored)?.email || "" : "";
    } catch {
      return "";
    }
  })();
  const currentUserEmail = (loggedInEmail || user?.email || storedUserEmail || "")
    .toString()
    .toLowerCase();
  const jwtPayload = parseJwtPayload(
    typeof window !== "undefined" ? localStorage.getItem("accessToken") : null
  );
  const currentUserIsAdmin = isAdminRole(jwtPayload, userRole ?? user?.role);
  const isEditingSelf =
    !!currentUserEmail && String(data?.email || "").toLowerCase() === currentUserEmail;
  const hideStatusAndRole = currentUserIsAdmin && isEditingSelf;

  const [storeResponse, setStoreResponse] = useState<StoreResponseProp>();

  const buildUserPayload = (values: any) => {
    const { mobileCountryCode, ...userValues } = values || {};
    return {
      ...userValues,
      mobileNumber: joinPhoneNumber(mobileCountryCode, values?.mobileNumber),
    };
  };

  // Drive the required markers below: hidden while the account status is
  // "ONBOARDING" (reactively watched), matching the conditional schema above.
  const selectedUserStatus = methods.watch("userStatus") ?? data.userStatus;
  const isOnboardingSelected = isOnboardingValue(selectedUserStatus);

  const selectedGender = methods.watch("gender") || data.gender;
  const responsibilityPronoun =
    typeof selectedGender === "string" &&
      selectedGender.toLowerCase() === "female"
      ? "She"
      : "He";

  /**
   * The exact payload the normal employee update sent, kept so the follow-up
   * status change can reuse it.
   * NOTE: react-hook-form only calls onSubmit once the schema passes, so this
   * always holds a complete, validated set of employee details.
   */
  const lastSubmittedValuesRef = useRef<any>(null);

  /**
   * NOTE: the "Change User Status" confirmation used to be opened from the
   * Submit button's onClick, which fires on every click regardless of whether
   * the form is valid. Opening it here instead keeps required-field validation
   * in force — the dialog can only appear after the schema has passed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit: SubmitHandler<any> = (formData: any) => {
    const normalizedValues = buildUserPayload(formData);
    lastSubmittedValuesRef.current = normalizedValues;

    // The "Change User Status" asset-collection confirmation is ONLY for
    // ACTIVE → INACTIVE (deactivation reclaims assets and removes the user from
    // projects). Any other transition (INACTIVE → ACTIVE, ONBOARDING → ACTIVE,
    // etc.) is applied by the normal update below with no extra pop-up.
    const isDeactivating =
      String(data.userStatus || "").toUpperCase() === "ACTIVE" &&
      String(formData.userStatus || "").toUpperCase() === "INACTIVE";

    if (isDeactivating) {
      setIsUpdate(true);
    }

    // Ensure UAN is explicitly structured if backend expects it inside profileDetails
    const payload = {
      ...normalizedValues,
      profileDetails: {
        ...(data.profileDetails || {}),
        UAN: formData.UAN,
      },
    };

    apiService
      .put("api/user", payload)
      .then((res) => {
        console.log("res", res);
        res.status === 200 && setStoreResponse(res.data);
        // Hand back the merged record so callers can reflect the update in place.
        onSuccess("Employee Details Updated successfully", { ...data, ...payload });
      })
      .catch((err) => {
        console.log("err :>> ", err);
        onError(err?.response?.data?.message || err.message || "Something went wrong");
      })
      .finally(() => {
        // Keep the modal open only for the deactivation asset-collection step;
        // otherwise close immediately.
        if (!isDeactivating) onClose();
      });
  };

  /**
   * NOTE: this used to build the payload as `{ ...data, userStatus }`, where
   * `data` is the employee row handed over by the list. That row does not carry
   * the full employee record (it is what leaves designation / DOJ / gender /
   * mobileNumber blank when the modal opens), and it also discards anything the
   * user just typed — so switching a user to Inactive posted an incomplete body
   * and the API rejected it for missing required details.
   *
   * The payload is now the same validated object the normal update sent, with
   * only userStatus swapped, so both requests carry identical employee fields.
   * The form values are used if a submitted payload is somehow unavailable, and
   * the row stays as the last-resort base so no unrelated key is dropped.
   */
  const userStatusteHandle = () => {
    const updatedStatus = methods.getValues("userStatus");
    const submittedValues =
      lastSubmittedValuesRef.current || buildUserPayload(methods.getValues());
    const updatedUser = { ...data, ...submittedValues, userStatus: updatedStatus };
    apiService
      .put("api/user?shouldRemoveUser=true", updatedUser)
      .then((res) => {
        if (res.status === 200) {
          onSuccess("User status updated successfully", updatedUser);
        } else {
          onError("Something went wrong while updating user status");
        }
      })
      .catch((error) => {
        console.error(error);
        onError(error?.response?.data?.message || error.message || "Something went wrong");
      })
      .finally(() => {
        onClose();
      });
  };

  const genderOptions: GenderOption[] = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];
  const options = [
    { value: "Web Developer", label: "Web Developer" },
    { value: "Java Developer", label: "Java Developer" },
    { value: "Developer", label: "Developer" },
    { value: "Windows developer", label: "Windows developer" },
    { value: "Programmer", label: "Programmer" },
    { value: "Systems Analyst", label: "Systems Analyst" },
    { value: "Software engineer", label: "Software engineer" },
    { value: "Fullstack Developer", label: "Fullstack Developer" },
    { value: "HR manager", label: "HR manager" },
    { value: "Android Developer", label: "Android Developer" },
    { value: "IOS Developer", label: "iOS Developer" },
    { value: "QA Engineer", label: "QA Engineer" },
    { value: "Test Manager", label: "Test Manager" },
    { value: "Test Engineer", label: "Test Engineer" },
    { value: "Test Analyst", label: "Test Analyst" },
    { value: "Test Automation Engineer", label: "Test Automation Engineer" },
  ];
  const statusOptions: GenderOption[] = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    // Onboarding is a user-status value; while it is selected the form is optional.
    { value: "ONBOARDING", label: "Onboarding" },
  ];
  const userOptions: userOptions[] = [
    { value: 1, label: "User" },
    { value: 2, label: "Admin" },
    { value: 3, label: "Sys_Admin" },
    { value: 4, label: "Manager" },
  ];

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="grid gap-x-4 grid-cols-2 ">
            <Input
              name="displayName"
              label="Employee Name"
              placeholder={`Enter Full Name`}
              required={!isOnboardingSelected}
              disabled
            />
            <Input
              name="email"
              label="Email Address"
              placeholder={`Enter Email Address`}
              required={!isOnboardingSelected}
              disabled
            />
            <Input
              name="DOJ"
              label="Date Of Joining"
              type="date"
              required={!isOnboardingSelected}
              disabled={data.DOJ ? true : false}
            />
            <PhoneNumberInput
              name="mobileNumber"
              countryCodeName="mobileCountryCode"
              label="Mobile Number"
              placeholder={`Enter Mobile Number`}
              required={!isOnboardingSelected}
              // Disable if user is inactive AND the initial mobile number is not empty
              disabled={isInactiveEmployee && !!data.mobileNumber}
            />
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Select Gender{" "}
                {!isOnboardingSelected && <span className="text-red-500">*</span>}
              </label>
              <Select
                {...methods.register("gender", { required: "Gender is required." })}
                options={genderOptions}
                placeholder="Select Gender"
                className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                // Disable if user is inactive AND gender is not empty
                isDisabled={isInactiveEmployee && !!data.gender}
                onChange={(selectedOption) => {
                  selectedOption &&
                    methods.setValue("gender", selectedOption.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                }}
                defaultValue={genderOptions.find(
                  (option) => option.value === data.gender
                )}
              />
              <div className="h-6 mt-1">
                {methods.formState.errors.gender?.message ? (
                  <p className="text-red-500 text-xs font-medium leading-tight">
                    {methods.formState.errors.gender.message as ReactNode}
                  </p>
                ) : (
                  <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                )}
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Designation{" "}
                {!isOnboardingSelected && <span className="text-red-500">*</span>}
              </label>
              <Select
                {...methods.register("designation", {
                  required: "Designation is required.",
                })}
                options={options}
                placeholder="Select Designation"
                className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                // Disable if user is inactive AND designation is not empty
                isDisabled={isInactiveEmployee && !!data.designation}
                onChange={(selectedOption) => {
                  selectedOption &&
                    methods.setValue("designation", selectedOption.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                }}
                defaultValue={options.find(
                  (option) => option.value === data.designation
                )}
              />
              <div className="h-6 mt-1">
                {methods.formState.errors.designation?.message ? (
                  <p className="text-red-500 text-xs font-medium leading-tight">
                    {methods.formState.errors.designation.message as ReactNode}
                  </p>
                ) : (
                  <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                )}
              </div>
            </div>

            {!hideStatusAndRole && (
              <>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Select Status <span className="text-red-500">*</span>
                  </label>
                  <Select
                    {...methods.register("userStatus", {
                      required: "Status is required.",
                    })}
                    options={statusOptions}
                    placeholder="Select Status"
                    className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    // Status is NEVER disabled because we need to allow reactivation
                    onChange={(selectedOption) => {
                      if (!selectedOption) return;
                      methods.setValue("userStatus", selectedOption.value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      // Status drives the optional/required gate — re-validate every
                      // field so errors clear (or re-appear) as it flips.
                      methods.trigger();
                    }}
                    defaultValue={statusOptions.find(
                      (option) => option.value === data.userStatus
                    )}
                  />
                  <div className="h-6 mt-1">
                    {methods.formState.errors.userStatus?.message ? (
                      <p className="text-red-500 text-xs font-medium leading-tight">
                        {methods.formState.errors.userStatus.message as ReactNode}
                      </p>
                    ) : (
                      <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                    )}
                  </div>
                </div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Select Role{" "}
                    {!isOnboardingSelected && <span className="text-red-500">*</span>}
                  </label>
                  <Select
                    {...methods.register("role", { required: "Role is required." })}
                    options={userOptions}
                    placeholder="Select Role"
                    className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    // Disable if user is inactive AND role is not empty
                    isDisabled={isInactiveEmployee && !!data.role}
                    onChange={(selectedOption) => {
                      selectedOption &&
                        methods.setValue("role", selectedOption.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                    }}
                    defaultValue={userOptions.find(
                      (option) => option.value === data.role
                    )}
                  />
                  <div className="h-6 mt-1">
                    {methods.formState.errors.role?.message ? (
                      <p className="text-red-500 text-xs font-medium leading-tight">
                        {methods.formState.errors.role.message as ReactNode}
                      </p>
                    ) : (
                      <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                    )}
                  </div>
                </div>
              </>
            )}

            <Input
              name="UAN"
              label="UAN number"
              placeholder={`Eg : 100123456789`}
              type="number"
              // UAN is optional for every employee — no required marker.
              // Disable if user is inactive AND UAN is not empty
              disabled={isInactiveEmployee && !!(data?.profileDetails?.UAN || data?.UAN)}
            />
          </div>

          <div className="flex justify-center gap-3 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-2.5 text-sm font-bold text-slate-600  bg-white  hover:bg-slate-100  focus:ring-4 focus:outline-none focus:ring-slate-200 dark:focus:ring-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 transition-all shadow-sm active:scale-95"
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ backgroundColor: colors.primary }}
              className={`text-white px-8 py-2.5 rounded-xl font-bold transition-all shadow-lg ${!isDirty || isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:opacity-90 active:scale-95"
                }`}
              disabled={!isDirty || isSubmitting}
            >
              Submit
            </button>
          </div>
        </form>
      </FormProvider>
      {isUpdate && (
        <GenericModal
          isOpen={isUpdate}
          onClose={() => setIsUpdate(false)}
          header="Change User Status "
        >
          <div className="relative  bg-white rounded-xl shadow dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
            <div className="p-4 md:p-5 text-center dark:text-white">
              <div className="rounded shadow p-4 mb-5 space-y-4 items-center justify-center">
                <h2 className="text-slate-600 font-medium text-xl dark:text-white">
                  Asset List{" "}
                </h2>
                <p className="text-slate-600 font-medium text-lg p-2 dark:text-white">
                  Could you please confirm if you are collecting the following
                  asset models?{" "}
                </p>
                <span className="text-slate-500 font-medium text-lg dark:text-white">
                  {" "}
                  {storeResponse && storeResponse.assetModels
                    ? responsibilityPronoun + " is responsible  for " +
                    storeResponse.assetModels.length +
                    ` Asset Models `
                    : ""}
                </span>
              </div>

              <div className="rounded shadow p-4 mb-5  items-center justify-center">
                <h2 className="text-slate-600 font-medium text-xl m-2 dark:text-white">
                  Projects
                </h2>
                <span className="text-slate-600 font-medium text-lg p-2 dark:text-white">
                  {" "}
                  The user has been removed from the project and will no longer
                  be involved.{" "}
                </span>
                <span className="text-slate-500 font-medium block text-lg dark:text-white">
                  {storeResponse && storeResponse.projectNames
                    ? responsibilityPronoun + " has been assigned a total of " +
                    storeResponse.projectNames.length +
                    ` Projects `
                    : ""}
                </span>
              </div>

              <button
                data-modal-hide="popup-modal"
                type="button"
                style={{ backgroundColor: colors.error }}
                className="text-white hover:opacity-90 focus:ring-4 focus:outline-none shadow-lg font-medium rounded-xl text-sm inline-flex items-center px-5 py-2.5 text-center transition-all active:scale-95"
                onClick={() => {
                  userStatusteHandle();
                }}
              >
                Yes, I'm sure
              </button>
              <button
                data-modal-hide="popup-modal"
                type="button"
                className="py-2.5 px-5 ms-3 text-sm font-medium text-slate-900 focus:outline-none bg-white rounded-xl border border-slate-200 hover:bg-slate-50 hover:text-primary focus:z-10 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600 dark:hover:text-white dark:hover:bg-slate-700 transition-all active:scale-95"
                onClick={() => setIsUpdate(false)}
              >
                No, cancel
              </button>
            </div>
          </div>
        </GenericModal>
      )}
    </>
  );
}